/* eslint-disable */
import {
  CopyOutlined, DeleteOutlined,
  EditOutlined,
  ExperimentOutlined, LinkOutlined, PlusOutlined,
  QrcodeOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import {
  Button, Card,
  message,
  Modal,
  Space, Spin, Table, TableProps, Tooltip, Typography
} from 'antd';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import ModalWarningEditTimeKeeping from 'common/components/ModalWarningEditTimeKeeping';
import {
  deletePayrollPeriodsService,
  getAllPayrollPeriodsService
} from 'common/services/extends/payrollPeriods';
import { getPermissionFromProjectByIdService } from 'common/services/extends/project';
import { ROUTE_PATHS } from 'common/utils/constant';
import { detectError, formatDateDDMMYYYY, handleCopy } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';
import QrCodeModal from 'extends/Program/qrCodeModal';
import TimeKeepingTableModal, { TableForm } from 'extends/TimeSheet/TimeKeepingTableModal';

export interface DataType {
  key?: number;
  name?: string;
  fromDate?: string;
  toDate?: string;
  code?: string;
  salaryIsCalculated?: boolean;
}

interface TimeKeepingTableProps {
  fromDate: string;
  toDate: string;
}

const TimeKeepingTable: React.FC<TimeKeepingTableProps> = ({ fromDate, toDate }) => {
  const { t } = useTranslation();
  const [qrCode, setQrCode] = useState('');
  const queryClient = useQueryClient();

  const rolesUser = useAppSelector((state) => state.auth.roles);

  const [searchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState<{
    id?: number,
    dataDefault?: TableForm,
    isOpen: boolean,
    type?: 'create' | 'update' | 'copy',
  }>({
    isOpen: false,
    type: undefined
  });

  const [modalWarningEditTimeKeeping, setModalWarningEditTimeKeeping] = useState<{
    isOpen: boolean;
    err?: {
      dates?: string[];
    };
  }>({
    isOpen: false,
    err: {}
  });

  const navigate = useNavigate();
  const projectId = searchParams.get('projectId') || '';
  const staffFullTimeId = searchParams.get('staffFullTimeId') || '';

  const queryKey = ['payroll-periods', staffFullTimeId, projectId];
  const { isLoading, data: payrollData } = useQuery(
    queryKey,
    () => getAllPayrollPeriodsService({
      _projectId: projectId,
      _staffFullTimeId: staffFullTimeId,
    }),
    {
      enabled: !!projectId && getPermission(rolesUser, roles.PAYROLL_PERIOD_INDEX),
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const { isLoading: isLoadingPermission, data: permission } = useQuery(
    ['project-permission-detail', projectId, staffFullTimeId],
    () => getPermissionFromProjectByIdService(
      projectId,
      {
        staffFullTimeId
      }
    ),
    {
      enabled: !!staffFullTimeId && !!projectId,
    }
  );

  const { mutate: deleteMutate, isLoading: deleteLoading } = useMutation(
    ['payroll-periods-delete'],
    async (ids: number[]) => deletePayrollPeriodsService({ ids }),
    {
      onSuccess: () => {
        message.success(t('message.deleteSuccess'));
        queryClient.invalidateQueries(queryKey);
      },
      onError: () => {
        message.error(t('message.deleteError'));
      }
    }
  );

  const columns: TableProps<DataType>['columns'] = [
    {
      title: t('timesheet.timeKeepingName'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('program.fromDate'),
      dataIndex: 'fromDate',
      key: 'fromDate',
      render: (data) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data, '/')}
        </Typography.Text>
      )
    },
    {
      title: t('program.toDate'),
      dataIndex: 'toDate',
      key: 'toDate',
      render: (data) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data, '/')}
        </Typography.Text>
      )
    },
    {
      title: t('system.qrCode'),
      key: 'qrcode',
      width: 100,
      align: 'center',
      render: (data) => (
        <Space>
          <Tooltip title={t('program.downloadQrCode')}>
            <Button
              icon={<QrcodeOutlined />}
              disabled={!data.code}
              onClick={() => setQrCode(`${data?.websiteDomain}/${data.code}`)}
            />
          </Tooltip>
          <Tooltip title={t('program.copyLink')}>
            <Button
              icon={<LinkOutlined />}
              disabled={!data.code}
              onClick={() => handleCopy({
                data: `${data?.websiteDomain}/${data?.code}`,
                messageFailed: t('message.failedCopied'),
                messageSuccess: t('message.successcopied')
              })}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      render: (data) => (
        <Space>
          <Tooltip title={t('timesheet.copyPeriod')}>
            <Button
              icon={<CopyOutlined />}
              disabled={!getPermission(rolesUser, roles.PAYROLL_PERIOD_COPY) || !permission?.canDo}
              onClick={() => setIsModalOpen({
                isOpen: true,
                type: 'copy',
                id: data.key,
                dataDefault: {
                  name: data.name,
                  rangeDate: [dayjs(data.fromDate).format('YYYY-MM-DD'), dayjs(data.toDate).format('YYYY-MM-DD')]
                }
              })}
            />
          </Tooltip>
          <Tooltip title={t('timesheet.viewPeriod')}>
            <Button
              icon={<ExperimentOutlined />}
              disabled={!getPermission(rolesUser, roles.PROJECT_INDEX)}
              onClick={() => navigate(`${ROUTE_PATHS.TIMEKEEPING_DETAIL}?projectId=${projectId}&staffFullTimeId=${staffFullTimeId}&payrollPeriodId=${data.key}`)}
            />
          </Tooltip>
          <Tooltip title={t('timesheet.viewStaffOfFeat')}>
            <Button
              icon={<UserAddOutlined />}
              disabled={!getPermission(rolesUser, roles.PROJECT_INDEX)}
              onClick={() => navigate(`${ROUTE_PATHS.SALARY_PROGRAM_DETAIL}?projectId=${projectId}&staffFullTimeId=${staffFullTimeId}&payrollPeriodId=${data.key}`)}
            />
          </Tooltip>
          <Tooltip title={t('timesheet.editPeriod')}>
            <Button
              icon={<EditOutlined />}
              disabled={!getPermission(rolesUser, roles.PAYROLL_PERIOD_UPDATE) || !permission?.canDo || data.salaryIsCalculated}
              onClick={() => setIsModalOpen({
                id: data.key,
                isOpen: true,
                type: 'update',
                dataDefault: {
                  name: data.name,
                  rangeDate: [dayjs(data.fromDate).format('YYYY-MM-DD'), dayjs(data.toDate).format('YYYY-MM-DD')]
                }
              })}
            />
          </Tooltip>
          <Tooltip title={t('timesheet.deletePeriod')}>
            <Button
              icon={<DeleteOutlined />}
              disabled={!getPermission(rolesUser, roles.PAYROLL_PERIOD_DESTROY) || !permission?.canDo || data.salaryIsCalculated}
              onClick={(e) => {
                if (e.stopPropagation) e.stopPropagation();
                Modal.confirm({
                  className: 't-pagetable_deleteLanguageModal',
                  autoFocusButton: 'cancel',
                  okText: t('system.ok'),
                  cancelText: t('system.cancel'),
                  cancelButtonProps: {
                    type: 'primary',
                  },
                  okButtonProps: {
                    type: 'default',
                  },
                  title: t('message.confirmDeleteRecord'),
                  onOk: () => deleteMutate([data.key]),
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const data: DataType[] = useMemo(() => {
    if (!payrollData) return [];

    return payrollData.data.map((item) => ({
      key: item.payrollPeriodData?.id,
      name: item.payrollPeriodData?.name || '',
      fromDate: item.payrollPeriodData?.startDate || '',
      toDate: item.payrollPeriodData?.endDate || '',
      code: item.payrollPeriodData?.code || '',
      websiteDomain: item.payrollPeriodData?.websiteDomain,
      salaryIsCalculated: item?.payrollPeriodData?.statusApprove === 2 || item?.payrollPeriodData?.statusApprove === 3,
    }));
  }, [payrollData]);

  const handleClose = () => {
    setIsModalOpen({
      ...isModalOpen,
      isOpen: false,
      id: undefined,
      dataDefault: undefined
    });
  };

  const nameTimeKeeping = useMemo(() => payrollData?.data?.map((item) => item.payrollPeriodData?.name || '')
    .filter((f) => !!f), [payrollData]);

  return (
    <div className="time-keeping-table">
      <Spin spinning={isLoading || deleteLoading || isLoadingPermission}>
        <Card
          title={(
            <Typography.Title level={4}>
              {t('timesheet.timekeepingPeriod')}
            </Typography.Title>
          )}
          extra={(
            <Button
              size="middle"
              type="default"
              icon={(<PlusOutlined />)}
              disabled={!getPermission(rolesUser, roles.PAYROLL_PERIOD_STORE) || !permission?.canDo}
              onClick={() => setIsModalOpen({
                isOpen: true,
                type: 'create'
              })}
            >
              {t('timesheet.addPeriod')}
            </Button>
          )}
        >
          <div className="t-pagetable_wrap">
            <Table<DataType>
              columns={columns}
              dataSource={data || []}
            />
          </div>
        </Card>
        <TimeKeepingTableModal
          handleClose={handleClose}
          isOpen={isModalOpen}
          projectDuration={[dayjs(fromDate), dayjs(toDate)]}
          dataDefault={isModalOpen.dataDefault}
          queryKey={queryKey}
          nameTimeKeeping={nameTimeKeeping}
          setModalWarningEditTimeKeeping={setModalWarningEditTimeKeeping}
        />
      </Spin>
      <QrCodeModal qrCode={qrCode} handleClose={() => setQrCode('')} />
      <ModalWarningEditTimeKeeping
        err={modalWarningEditTimeKeeping.err}
        isOpen={modalWarningEditTimeKeeping.isOpen}
        onClose={() => setModalWarningEditTimeKeeping({ isOpen: false, err: {} })}
      />
    </div>
  );
};

export default TimeKeepingTable;
